import classNames from 'classnames';
import _ from 'lodash';
import React from 'react';

import AccessibleDiv from 'components/AccessibleDiv/AccessibleDiv';
import ActionIcon from 'components/ActionIcon';
import { ActionsColor } from 'components/ArtboardLayoutActions/constants';
import { IconType } from 'components/Icon/constants';
import { AddCellDirection, SplitCellDirection } from 'const';
import { IArtboardCellActionsProps, IArtboardCellActionsState } from './models';
import styles from './styles.module.scss';

export default class ArtboardCellActions extends React.PureComponent<IArtboardCellActionsProps, IArtboardCellActionsState> {
  readonly state: IArtboardCellActionsState = {
    actionsExpanded: false,
    showActions: false,
  };

  private readonly showActions = (): void => {
    if (!this.props.areActionsDisabled) {
      this.setState({ showActions: true });
    }
  };

  private readonly expandActions = (): void => {
    if (!this.props.areActionsDisabled) {
      this.setState({ showActions: true, actionsExpanded: true });
    }
  };

  private readonly collapseActions = (): void => {
    this.setState({ actionsExpanded: false });
  };

  private readonly hideActions = (): void => {
    this.setState({ showActions: false, actionsExpanded: false });
  };

  render(): JSX.Element {
    const {
      addCell,
      canDrop,
      cellWidth,
      children,
      component,
      copyContent,
      copyingEnabled,
      deleteCell,
      deletingDisabled,
      isAddingDisabled,
      isEditMode,
      isOver,
      isSelected,
      onSelectHandler,
      outlineOnly,
      splitCell,
      dataTestId,
    } = this.props;
    const {
      actionsExpanded,
      showActions,
    } = this.state;

    const cellClassName = classNames(
      styles.ArtboardCellsActions,
      {
        [styles.showActions]: showActions || isSelected || isEditMode,
        [styles.over]: isOver && showActions,
        [styles.canDrop]: canDrop,
        [styles.outlineOnly]: outlineOnly,
        [styles.large]: _.inRange(cellWidth, 33, 121),
        [styles.medium]: _.inRange(cellWidth, 11, 33),
        [styles.small]: _.inRange(cellWidth, 0, 11),
      },
    );
    const actionsDataTestId = `${dataTestId}-actions`;
    const actionsRightDataTestId = `${dataTestId}-actionsRight`;
    const actionsLeftDataTestId = `${dataTestId}-actionsLeft`;
    const actionsTopDataTestId = `${dataTestId}-actionsTop`;
    const actionsBottomDataTestId = `${dataTestId}-actionsBottom`;

    return (
      <AccessibleDiv
        ref={component}
        className={cellClassName}
        onMouseOver={this.showActions}
        onMouseLeave={this.hideActions}
        onMouseDown={onSelectHandler}
        data-testid={actionsDataTestId}
      >
        {children}
        <div className={styles.rightActions} data-testid={actionsRightDataTestId}>
          {
            actionsExpanded
              ?
              <AccessibleDiv className={styles.expanded} onMouseLeave={this.collapseActions}>
                <ActionIcon
                  backgroundColor={ActionsColor.ORANGE}
                  type={IconType.DELETE}
                  onClick={deleteCell}
                  size="xs"
                  isDisabled={deletingDisabled}
                  color="secondary"
                  dataTestId={`${actionsRightDataTestId}-actionIcon${IconType.DELETE}`}
                />
                {
                  copyingEnabled &&
                  <ActionIcon
                    backgroundColor={ActionsColor.ORANGE}
                    type={IconType.COPY}
                    size="xs"
                    onClick={copyContent}
                    color="secondary"
                    dataTestId={`${actionsRightDataTestId}-actionIcon${IconType.COPY}`}
                  />
                }
                <ActionIcon
                  backgroundColor={ActionsColor.ORANGE}
                  type={IconType.ADD}
                  size="xs"
                  onClick={(): void => splitCell(SplitCellDirection.RIGHT)}
                  isDisabled={isAddingDisabled}
                  color="secondary"
                  dataTestId={`${actionsRightDataTestId}-actionIcon${IconType.ADD}`}
                />
              </AccessibleDiv>
              :
              <ActionIcon
                backgroundColor={ActionsColor.ORANGE}
                type={IconType.MENU}
                size="xs"
                color="secondary"
                onMouseEnter={this.expandActions}
                dataTestId={`${actionsRightDataTestId}-actionIcon${IconType.MENU}`}
              />
          }
        </div>
        <div className={styles.leftActions} data-testid={actionsLeftDataTestId}>
          <ActionIcon
            backgroundColor={ActionsColor.ORANGE}
            type={IconType.ADD}
            size="xs"
            onClick={(): void => splitCell(SplitCellDirection.LEFT)}
            isDisabled={isAddingDisabled}
            color="secondary"
            dataTestId={`${actionsLeftDataTestId}-actionIcon${IconType.ADD}`}
          />
        </div>
        <div className={styles.topActions} data-testid={actionsTopDataTestId}>
          <ActionIcon
            backgroundColor={ActionsColor.ORANGE}
            type={IconType.ADD}
            size="xs"
            onClick={(): void => addCell(AddCellDirection.TOP)}
            color="secondary"
            dataTestId={`${actionsTopDataTestId}-actionIcon${IconType.ADD}`}
          />
        </div>
        <div className={styles.bottomActions} data-testid={actionsBottomDataTestId}>
          <ActionIcon
            backgroundColor={ActionsColor.ORANGE}
            type={IconType.ADD}
            size="xs"
            onClick={(): void => addCell(AddCellDirection.BOTTOM)}
            color="secondary"
            dataTestId={`${actionsBottomDataTestId}-actionIcon${IconType.ADD}`}
          />
        </div>
      </AccessibleDiv>
    );
  }
}
